import { useMemo } from 'react'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'
import { getCheapestBedbankHotelOnlyRate, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { useAppSelector } from 'hooks/reduxHooks'

interface Result {
  /** Cheapest bedbank hotel-only rate for the given dates/occupancy */
  hotelOnlyRate: App.BedbankRate | undefined
  /**
   * Display total price of the cheapest hotel-only rate. This is the price the metasearch discount
   * request is keyed to, so anything reading the discount must pass this exact value.
   */
  discountBasePrice: number | undefined
}

/**
 * Resolves the cheapest bedbank hotel-only rate and its display total price for a metasearch offer.
 * Both the discount fetch (`BedbankOfferContainer`) and the JSON-LD lookup (`HotelStructuredData`)
 * derive the price here, so the metasearch discount request key stays identical between them.
 */
export default function useBedbankMetasearchDiscountBasePrice(
  offer: App.BedbankOffer,
  rooms: Array<App.Occupants>,
  checkInDate: string | undefined,
  checkOutDate: string | undefined,
): Result {
  const discountRateKey = useMemo(
    () => (checkInDate && checkOutDate && rooms ? getBedbankRateKey(rooms, checkInDate, checkOutDate) : undefined),
    [checkInDate, checkOutDate, rooms],
  )
  const discountRates = useAppSelector(state =>
    discountRateKey ? state.offer.bedbankOfferRates[offer.id]?.[discountRateKey] : undefined,
  )
  const hotelOnlyRate = useMemo(
    () => getCheapestBedbankHotelOnlyRate(discountRates ?? []),
    [discountRates],
  )
  const discountBasePrice = useMemo(() => {
    if (!checkInDate || !rooms || !hotelOnlyRate) {
      return undefined
    }
    const propertyFees = hotelOnlyRate.totals.propertyFees ?? 0
    return getDisplayTotalPrice({
      hotelPrice: hotelOnlyRate.totals.inclusive + propertyFees,
      hotelMemberPrice: hotelOnlyRate.totals.memberInclusive ? hotelOnlyRate.totals.memberInclusive + propertyFees : 0,
      hotelValue: hotelOnlyRate.value,
      checkIn: checkInDate,
      rooms,
      displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
    }).totals.price
  }, [checkInDate, rooms, hotelOnlyRate, offer.displayPricingAsPerPerson])

  return { hotelOnlyRate, discountBasePrice }
}
